import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  Row,
  Col,
  Button,
  Input,
  FormFeedback,
  Collapse,
} from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../../../../components/FormField/FormField";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { ChevronDown, ChevronUp } from "react-feather";

/* ── Validation: first name + last name + at least email OR phone ─ */
const validation = Yup.object().shape({
  first_name: Yup.string().required("First name is required."),
  last_name: Yup.string().required("Last name is required."),
  email: Yup.string()
    .test(
      "email-or-phone",
      "Please enter a valid email",
      function (value) {
        const { phone } = this.parent;
        if (!value && !phone) return this.createError({ message: "At least email or phone is required." });
        if (!value) return true; // phone provided, email optional
        return /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(value);
      }
    ),
  phone: Yup.string().test(
    "phone-or-email",
    "At least email or phone is required.",
    function (value) {
      const { email } = this.parent;
      if (!value && !email) return false;
      return true;
    }
  ),
});

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  // optional fields
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  zip: "",
  country_id: "",
  notes: "",
};

const CreateModal = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const toggle = () => {
    setIsOpen((v) => !v);
    // reset collapse state on close
    if (isOpen) {
      setShowAddress(false);
      setShowNotes(false);
    }
  };

  return (
    <>
      <Button
        className="btn btn-app"
        type="button"
        onClick={(e) => { e.preventDefault(); toggle(); }}
      >
        + Add customer
      </Button>

      <Modal
        isOpen={isOpen}
        toggle={toggle}
        className="event-modal modal-lg modal-dialog"
      >
        <ModalBody>
          <div className="box_content">
            <div className="rd_inputselectheadermodalsetting">
              <button className="rd_colsebtn" onClick={toggle} />
            </div>

            <h5 className="rd_modalheadertext rd_modalheadertextnm">Add Customer</h5>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 20 }}>
              First name, last name, and at least one contact method (email or phone) are required.
            </p>

            <Formik
              validationSchema={validation}
              initialValues={initialValues}
              enableReinitialize
              onSubmit={(values, { resetForm }) => {
                const { onAddRecord } = props;
                toggle();
                onAddRecord(values);
                resetForm();
              }}
            >
              {({ values, errors, touched, handleChange, setFieldValue, setFieldTouched }) => (
                <Form>
                  {/* ── Required fields ── */}
                  <Row>
                    <Col lg="6" md="6">
                      <label className="same-label">First Name *</label>
                      <FormField
                        type="text"
                        name="first_name"
                        placeholder="First Name"
                        value={values.first_name}
                        errors={errors}
                        touched={touched}
                        onChange={({ target }) =>
                          handleChange({ target: { name: "first_name", value: target.value } })
                        }
                      />
                    </Col>
                    <Col lg="6" md="6">
                      <label className="same-label">Last Name *</label>
                      <FormField
                        type="text"
                        name="last_name"
                        placeholder="Last Name"
                        value={values.last_name}
                        errors={errors}
                        touched={touched}
                        onChange={({ target }) =>
                          handleChange({ target: { name: "last_name", value: target.value } })
                        }
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col lg="6" md="6">
                      <label className="same-label">
                        Email {!values.phone && "*"}
                      </label>
                      <FormField
                        type="text"
                        name="email"
                        placeholder="Email"
                        value={values.email}
                        errors={errors}
                        touched={touched}
                        onChange={({ target }) =>
                          handleChange({ target: { name: "email", value: target.value } })
                        }
                      />
                    </Col>
                    <Col lg="6" md="6">
                      <label className="same-label">
                        Phone {!values.email && "*"}
                      </label>
                      <PhoneInput
                        containerClass="custom_phn_inp"
                        country={"us"}
                        value={values.phone}
                        onBlur={() => setFieldTouched("phone", true)}
                        onChange={(value) =>
                          handleChange({ target: { name: "phone", value } })
                        }
                      />
                      {errors.phone && touched.phone && (
                        <FormFeedback className="d-block">{errors.phone}</FormFeedback>
                      )}
                      {errors.email && touched.email && !values.phone && (
                        <FormFeedback className="d-block">{errors.email}</FormFeedback>
                      )}
                    </Col>
                  </Row>

                  {/* ── Address (collapsible) ── */}
                  <div className="crm-create-section">
                    <button
                      type="button"
                      className="crm-create-section__toggle"
                      onClick={() => setShowAddress((v) => !v)}
                    >
                      {showAddress ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      Address &amp; Location
                      <span className="crm-create-section__optional">optional</span>
                    </button>
                    <Collapse isOpen={showAddress}>
                      <Row className="mt-3">
                        <Col md="12" className="pt-2">
                          <div className="form-group event-form-group">
                            <label>Address Line 1</label>
                            <Input
                              type="textarea"
                              className="form-control"
                              name="address_line_1"
                              placeholder="Street address"
                              value={values.address_line_1}
                              onChange={({ target }) =>
                                handleChange({ target: { name: "address_line_1", value: target.value } })
                              }
                            />
                          </div>
                        </Col>
                        <Col md="12" className="pt-2">
                          <div className="form-group event-form-group">
                            <label>Address Line 2</label>
                            <Input
                              type="textarea"
                              className="form-control"
                              name="address_line_2"
                              placeholder="Apartment, suite, unit…"
                              value={values.address_line_2}
                              onChange={({ target }) =>
                                handleChange({ target: { name: "address_line_2", value: target.value } })
                              }
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="6" md="6">
                          <label className="same-label">City</label>
                          <FormField
                            type="text" name="city" placeholder="City"
                            errors={errors} touched={touched} value={values.city}
                            onChange={({ target }) => handleChange({ target: { name: "city", value: target.value } })}
                          />
                        </Col>
                        <Col lg="6" md="6">
                          <label className="same-label">State / Region</label>
                          <FormField
                            type="text" name="state" placeholder="State"
                            errors={errors} touched={touched} value={values.state}
                            onChange={({ target }) => handleChange({ target: { name: "state", value: target.value } })}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="6" md="6">
                          <label className="same-label">ZIP / Postal Code</label>
                          <FormField
                            type="text" name="zip" placeholder="ZIP"
                            errors={errors} touched={touched} value={values.zip}
                            onChange={({ target }) => handleChange({ target: { name: "zip", value: target.value } })}
                          />
                        </Col>
                        <Col lg="6" md="6">
                          <label className="same-label">Country</label>
                          <FormField
                            placeholder="Country"
                            showPlaceholder={true}
                            type="select"
                            name="country_id"
                            errors={errors}
                            touched={touched}
                            options={props.countries}
                            value={(values.country_id || "").toString()}
                          />
                        </Col>
                      </Row>
                    </Collapse>
                  </div>

                  {/* ── Notes (collapsible) ── */}
                  <div className="crm-create-section">
                    <button
                      type="button"
                      className="crm-create-section__toggle"
                      onClick={() => setShowNotes((v) => !v)}
                    >
                      {showNotes ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      Internal Notes
                      <span className="crm-create-section__optional">optional</span>
                    </button>
                    <Collapse isOpen={showNotes}>
                      <div className="mt-3">
                        <Input
                          type="textarea"
                          rows={3}
                          className="form-control"
                          name="notes"
                          placeholder="Notes visible to your team only…"
                          value={values.notes}
                          onChange={({ target }) =>
                            handleChange({ target: { name: "notes", value: target.value } })
                          }
                        />
                      </div>
                    </Collapse>
                  </div>

                  {/* ── Actions ── */}
                  <div className="rd_cancelappbtnmodal mt-3">
                    <button type="button" className="rd_cancel" onClick={toggle}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-app">
                      Save
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </ModalBody>
      </Modal>

      {/* inline styles for collapsible sections */}
      <style>{`
        .crm-create-section {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-top: 14px;
          overflow: hidden;
        }
        .crm-create-section__toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
          background: #f9fafb;
          border: none;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          text-align: left;
          color: #374151;
        }
        .crm-create-section__toggle:hover { background: #f3f4f6; }
        .crm-create-section__optional {
          font-size: 11px;
          font-weight: 400;
          color: #9ca3af;
          margin-left: auto;
        }
        .crm-create-section .row, .crm-create-section > .mt-3 { padding: 4px 14px 12px; }
      `}</style>
    </>
  );
};

export default CreateModal;
